import type { AdminQuestClaim, QuestClaimStatus } from '@src/dto/QuestClaims';
import { useEffect, useState } from 'react';
import { Button } from '../../components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { formatSkrBaseUnits, parseSkrUnitsToBaseUnits } from './skr';

export const CLAIM_STATUS_OPTIONS: QuestClaimStatus[] = [
  'Unrevealed',
  'Ready',
  'Claimed',
];

export interface ClaimRoundOption {
  id: string;
  label: string;
}

export interface ClaimFormValues {
  roundId: string;
  walletId: string;
  tierCode: string;
  tierRank: string;
  amountSkr: string;
  status: QuestClaimStatus;
  claimTransactionSignature: string;
}

const EMPTY_FORM: ClaimFormValues = {
  roundId: '',
  walletId: '',
  tierCode: '',
  tierRank: '',
  amountSkr: '',
  status: 'Ready',
  claimTransactionSignature: '',
};

const inputClassName =
  'w-full rounded-lg border border-neutral-700/70 bg-neutral-800/60 px-3 py-2.5 text-sm text-white transition-colors hover:border-neutral-600 focus:border-teal-500 focus:outline-none focus:ring-1 focus:ring-teal-500';

function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: React.ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="block text-xs font-medium uppercase tracking-wide text-neutral-500">
        {label}
      </span>
      {children}
      {hint ? (
        <span className="block text-xs text-neutral-500">{hint}</span>
      ) : null}
    </label>
  );
}

interface QuestClaimModalProps {
  isOpen: boolean;
  mode: 'assign' | 'edit';
  claim: AdminQuestClaim | null;
  rounds: ClaimRoundOption[];
  isSubmitting: boolean;
  onClose: () => void;
  onSubmit: (values: ClaimFormValues) => void;
}

export function QuestClaimModal({
  isOpen,
  mode,
  claim,
  rounds,
  isSubmitting,
  onClose,
  onSubmit,
}: QuestClaimModalProps) {
  const [form, setForm] = useState<ClaimFormValues>(EMPTY_FORM);

  useEffect(() => {
    if (!isOpen) {
      return;
    }
    if (mode === 'edit' && claim) {
      setForm({
        roundId: claim.roundId,
        walletId: claim.walletId,
        tierCode: claim.tierCode,
        tierRank: claim.tierRank === null ? '' : String(claim.tierRank),
        amountSkr: formatSkrBaseUnits(claim.amountBaseUnits),
        status: claim.status,
        claimTransactionSignature: claim.claimTransactionSignature ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
  }, [isOpen, mode, claim]);

  const setField = <K extends keyof ClaimFormValues>(
    key: K,
    value: ClaimFormValues[K],
  ) => {
    setForm((prev) => ({ ...prev, [key]: value }));
  };

  const parsedAmountBaseUnits = parseSkrUnitsToBaseUnits(form.amountSkr);
  const amountValid =
    parsedAmountBaseUnits !== null && parsedAmountBaseUnits !== '';
  const tierRankValid =
    form.tierRank === '' || Number.isInteger(Number(form.tierRank));

  const requiredFilled =
    (mode === 'edit' || form.roundId !== '') &&
    form.walletId.trim() !== '' &&
    form.tierCode.trim() !== '';

  const canSubmit =
    requiredFilled && amountValid && tierRankValid && !isSubmitting;

  const isEdit = mode === 'edit';

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(open) => {
        if (!open) {
          onClose();
        }
      }}
    >
      <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEdit ? 'Edit claim' : 'Assign claim'}</DialogTitle>
          <DialogDescription>
            {isEdit
              ? 'Update this allocation. Claim timestamps are set automatically when the status changes.'
              : 'Create or overwrite the allocation for a wallet in a round (one claim per wallet per round).'}
          </DialogDescription>
        </DialogHeader>

        <DialogBody className="space-y-4">
          {isEdit ? (
            <div className="grid grid-cols-2 gap-3 rounded-lg border border-neutral-800 bg-neutral-800/40 p-3 text-sm">
              <div>
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  Round
                </div>
                <div className="text-white">
                  {claim ? `Round #${claim.roundNumber}` : '—'}
                </div>
              </div>
              <div className="min-w-0">
                <div className="text-xs uppercase tracking-wide text-neutral-500">
                  Wallet
                </div>
                <div className="truncate font-mono text-white">
                  {claim?.walletId}
                </div>
              </div>
            </div>
          ) : (
            <>
              <Field label="Round">
                <select
                  aria-label="Round"
                  value={form.roundId}
                  onChange={(e) => setField('roundId', e.target.value)}
                  className={inputClassName}
                  style={{ colorScheme: 'dark' }}
                >
                  <option value="">Select a round…</option>
                  {rounds.map((round) => (
                    <option key={round.id} value={round.id}>
                      {round.label}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Wallet" hint="The claimant wallet address.">
                <input
                  type="text"
                  aria-label="Wallet"
                  value={form.walletId}
                  onChange={(e) => setField('walletId', e.target.value)}
                  placeholder="Wallet address"
                  className={`${inputClassName} font-mono`}
                />
              </Field>
            </>
          )}

          <div className="grid grid-cols-2 gap-3">
            <Field label="Tier code" hint="Free-form code (e.g. tier-1).">
              <input
                type="text"
                aria-label="Tier code"
                value={form.tierCode}
                onChange={(e) => setField('tierCode', e.target.value)}
                className={inputClassName}
              />
            </Field>
            <Field label="Tier rank" hint="Optional integer.">
              <input
                type="number"
                aria-label="Tier rank"
                value={form.tierRank}
                onChange={(e) => setField('tierRank', e.target.value)}
                className={inputClassName}
              />
            </Field>
          </div>

          <Field label="Amount (SKR)" hint="Reward amount in SKR.">
            <input
              type="text"
              inputMode="decimal"
              aria-label="Amount (SKR)"
              value={form.amountSkr}
              onChange={(e) => setField('amountSkr', e.target.value)}
              placeholder="0"
              className={`${inputClassName} ${
                form.amountSkr && !amountValid ? 'border-red-500/70' : ''
              }`}
            />
          </Field>

          <Field label="Status">
            <select
              aria-label="Status"
              value={form.status}
              onChange={(e) =>
                setField('status', e.target.value as QuestClaimStatus)
              }
              className={inputClassName}
              style={{ colorScheme: 'dark' }}
            >
              {CLAIM_STATUS_OPTIONS.map((status) => (
                <option key={status} value={status}>
                  {status}
                </option>
              ))}
            </select>
          </Field>

          {isEdit ? (
            <Field
              label="Claim transaction signature"
              hint="On-chain signature once claimed (optional)."
            >
              <input
                type="text"
                aria-label="Claim transaction signature"
                value={form.claimTransactionSignature}
                onChange={(e) =>
                  setField('claimTransactionSignature', e.target.value)
                }
                className={`${inputClassName} font-mono`}
              />
            </Field>
          ) : null}
        </DialogBody>

        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={onClose}
            disabled={isSubmitting}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => onSubmit(form)}
            disabled={!canSubmit}
            className="bg-teal-600 text-white hover:bg-teal-700"
          >
            {isSubmitting
              ? 'Saving…'
              : isEdit
                ? 'Save changes'
                : 'Assign claim'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
